import type { InvestorSearchDataInput } from '@workspace/api-backend'

export interface SearchCriteriaShape {
  stages: Array<'preseed' | 'seed'>
  phrases: string[]
  regions?: NonNullable<InvestorSearchDataInput['criteria']['regions']>
  sectors?: string[]
}

export type CriteriaChip =
  | { key: string; kind: 'stage'; stages: Array<'preseed' | 'seed'> }
  | { key: string; kind: 'region'; region: string }
  | { key: string; kind: 'sector'; sector: string }
  | { key: string; kind: 'phrase'; text: string }

/** The "Searching for" chips of an answer: stages, regions, sectors, then each phrase, in the order the sentence gave them. */
export function criteriaChips(criteria: SearchCriteriaShape): CriteriaChip[] {
  return [
    ...(criteria.stages.length > 0
      ? [{ key: 'stage', kind: 'stage' as const, stages: criteria.stages }]
      : []),
    ...(criteria.regions ?? []).map(
      (region, index): CriteriaChip => ({ key: `region:${index}`, kind: 'region', region })
    ),
    ...(criteria.sectors ?? []).map(
      (sector, index): CriteriaChip => ({ key: `sector:${index}`, kind: 'sector', sector })
    ),
    ...criteria.phrases.map(
      (text, index): CriteriaChip => ({ key: `phrase:${index}`, kind: 'phrase', text })
    ),
  ]
}

/** A search keeps at least one criterion: the last chip has no remove. */
export function canRemoveChip(criteria: SearchCriteriaShape): boolean {
  return criteriaChips(criteria).length > 1
}

/** The criteria left when one chip is removed. The chip's key says which stage, or which position in its list. */
export function removeChip(criteria: SearchCriteriaShape, chip: CriteriaChip): SearchCriteriaShape {
  if (chip.kind === 'stage') {
    return { ...criteria, stages: [] }
  }
  const index = Number(chip.key.slice(chip.key.indexOf(':') + 1))
  const without = <T>(items: T[] | undefined) =>
    (items ?? []).filter((_, position) => position !== index)
  if (chip.kind === 'region') return { ...criteria, regions: without(criteria.regions) }
  if (chip.kind === 'sector') return { ...criteria, sectors: without(criteria.sectors) }
  return { ...criteria, phrases: without(criteria.phrases) }
}

/** The term a typed removal names ("Remove “running pilots”", "without Europe"), or null when the text is not one. */
export function removalTerm(text: string): string | null {
  const match = /^(?:remove|without|drop)\s+(.+)$/i.exec(text.trim())
  const term = match?.[1]?.replace(/^["“‘']+|["”’']+$/g, '').trim()
  return term ? term : null
}

/**
 * The line the focus offer shows: what the search would become the focus of, for example
 * "Pre-seed or seed · Europe · mentions “pilots”". Labels come from the caller, so it stays pure.
 */
export function focusSummary(
  criteria: SearchCriteriaShape,
  labels: { stage: (stage: 'preseed' | 'seed') => string; region: (code: string) => string }
): string {
  const stages = criteria.stages.map(labels.stage).join(' or ')
  const regions = (criteria.regions ?? []).map(labels.region).join(' or ')
  const sectors = (criteria.sectors ?? []).join(' or ')
  const phrases = criteria.phrases.map((text) => `mentions “${text}”`)
  return [stages, regions, sectors, ...phrases].filter((part) => part !== '').join(' · ')
}
